import saveGdprTcfTCString from './saveGdprTcfTCString';
import gdprTcfStore from '../store/gdprTcfStore';
import generateTcfString from '../utils/generateTcfString';
import { TCModel, TCString } from '@iabtcf/core';
import { logUsage } from 'owa-analytics';
import {
    lazyGetServerOptionsForFeature,
    GdprAdsV3Options,
    OwsOptionsFeatureType,
} from 'owa-outlook-service-options';

export default async function loadGdprTcfTCString(): Promise<string> {
    // if gdprTcfString is not null, load the string from store instead of option
    if (gdprTcfStore.gdprTcfString != null) {
        return gdprTcfStore.gdprTcfString;
    }

    // The vendorListVersion is the same as the vendorlist.json under the resource file
    // We put the version here so that we do not need to pull down the whole vendorlist.json every time when we load the Ad consent string
    const checkedInVendorListVersion = 56;

    let currentEncodedTCString;

    const getServerOptionsForFeature = await lazyGetServerOptionsForFeature.import();

    const response = await getServerOptionsForFeature<GdprAdsV3Options>(
        OwsOptionsFeatureType.GdprAdsV3
    );

    if (response == null || response.encodedTCString == null || response.encodedTCString == '') {
        return null;
    }

    currentEncodedTCString = response.encodedTCString;

    let consentDataTCModel: TCModel;

    // Judge the saved consent data string is a valid Outlook.com consent string.
    // 1. Invalid consent string format will cause the exception in the ConsentString sdk
    // 2. CmpId must be 168.
    // For any invalid consent string, we will return null to let the caller reset the consent string
    try {
        consentDataTCModel = TCString.decode(currentEncodedTCString);
    } catch (err) {
        logUsage('InvalidFormatV3ConsentTCFDataString', {
            consentString: currentEncodedTCString,
            errorMessage: err.message,
        });
        return null;
    }

    if (consentDataTCModel.cmpId != 168) {
        logUsage('InvalidCmpIdConsentDataString', { consentString: currentEncodedTCString });
        return null;
    }

    // If the saved consent string's vendor list version is smaller than the check in vendor list version, we will re-generate and save a new consent string
    if (consentDataTCModel.vendorListVersion < checkedInVendorListVersion) {
        const gdprTcfPurposeFeatureCurrentBits = {
            allStoreAndAccessDevice: response.allStoreAndAccessDevice,
            allowPersonalisedAdsSelectBasicAd: response.allowPersonalisedAdsSelectBasicAd,
            allowPersonalisedAdsCreatePersonalisedAdsProfile:
                response.allowPersonalisedAdsCreatePersonalisedAdsProfile,
            allowPersonalisedAdsSelectPersonalisedAds:
                response.allowPersonalisedAdsSelectPersonalisedAds,
            allowPersonalisedAdsCreatePersonalisedContentProfile:
                response.allowPersonalisedAdsCreatePersonalisedContentProfile,
            allowPersonalisedAdsSelectPersonalisedContent:
                response.allowPersonalisedAdsSelectPersonalisedContent,
            allowPersonalisedAdsMeasureAdPerf: response.allowPersonalisedAdsMeasureAdPerf,
            allowPersonalisedAdsMeasureContentPerformance:
                response.allowPersonalisedAdsMeasureContentPerformance,
            allowPersonalisedAdsApplyMarketResearch:
                response.allowPersonalisedAdsApplyMarketResearch,
            allowDevelopAndImproveProduct: response.allowDevelopAndImproveProduct,
            allowPreciseGeoDataAndIdentifyDeviceGeoData:
                response.allowPreciseGeoDataAndIdentifyDeviceGeoData,
            allowPreciseGeoDataAndIdentifyDeviceScanDevice:
                response.allowPreciseGeoDataAndIdentifyDeviceScanDevice,
        };

        let disselectedVendorIdArray = [];
        if (response?.disselectedVendorId?.length > 0) {
            disselectedVendorIdArray = response.disselectedVendorId.slice() as number[];
        }

        const allowedLegitimateInterestsBits = !(response?.selectedVendorId?.indexOf(1) > -1);

        const currentEncodedTCString = await generateTcfString(
            gdprTcfPurposeFeatureCurrentBits,
            allowedLegitimateInterestsBits,
            disselectedVendorIdArray
        );

        await saveGdprTcfTCString(currentEncodedTCString);
    }

    return currentEncodedTCString;
}
